import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useLocalSearchParams, useRouter, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    KeyboardAvoidingView,
    Platform,
    SafeAreaView,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { ActivityIndicator, Snackbar, Text } from "react-native-paper";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DraftCard, type CardSelections } from "@/components/medications/DraftCard";
import { AllDraftsComplete } from "@/components/medications/EmptyStates";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useDraftEnrichment } from "@/hooks/useDraftEnrichment";
import {
    fetchDrafts,
    fetchMedications,
    promoteDraft,
    deleteDraft,
    type MedicationDraft,
    type Medication
} from "@/lib/api";
import { checkDuplicate } from "@/lib/medication-utils";
import { colors, fonts, shadows, spacing } from "@/lib/theme";

const MAX_CONCURRENT_LLM = 2;

// ── Per-card enrichment wrapper ──
// Renders a DraftCard with its enrichment result

function EnrichedDraftCard({
    draft,
    visitId,
    enabled,
    onLLMStart,
    onLLMEnd,
    onSaved,
    onDiscard,
    onSelectionChange,
    saving
}: {
    draft: MedicationDraft;
    visitId: string;
    enabled: boolean;
    onLLMStart: () => void;
    onLLMEnd: () => void;
    onSaved: (draftId: string) => Promise<boolean>;
    onDiscard: () => void;
    onSelectionChange: (sel: CardSelections) => void;
    saving?: boolean;
}) {
    const enrichment = useDraftEnrichment(draft, visitId, enabled, onLLMStart, onLLMEnd);

    return (
        <DraftCard
            draft={draft}
            enrichment={enrichment}
            onSaved={onSaved}
            onDiscard={onDiscard}
            onSelectionChange={onSelectionChange}
            saving={saving}
        />
    );
}

// ── Main screen ──

export default function DraftReviewScreen() {
    const { id: visitId } = useLocalSearchParams<{ id: string }>();
    const router = useRouter();

    // Prevent double-navigation from auto-navigate timer + manual back press race
    const hasNavigatedRef = useRef(false);

    const [drafts, setDrafts] = useState<MedicationDraft[]>([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState<string | null>(null);
    const [initialCount, setInitialCount] = useState(0);
    const [reviewedCount, setReviewedCount] = useState(0);
    const [savingDraftId, setSavingDraftId] = useState<string | null>(null);
    const { dialogState, showDialog, dismissDialog } = useConfirmDialog();

    // ── Throttling (Decision 1: useState for re-render propagation) ──
    const [activeEnrichments, setActiveEnrichments] = useState(0);
    const screenMountedRef = useRef(true);
    useEffect(() => {
        return () => {
            screenMountedRef.current = false;
        };
    }, []);
    const onLLMStart = useCallback(() => {
        if (screenMountedRef.current) {
            setActiveEnrichments((n) => n + 1);
        }
    }, []);
    const onLLMEnd = useCallback(() => {
        if (screenMountedRef.current) {
            setActiveEnrichments((n) => Math.max(0, n - 1));
        }
    }, []);

    // ── Card selections (Decision 10: useRef for data, useState only for completeCount) ──
    const cardSelectionsRef = useRef<Map<string, CardSelections>>(new Map());
    const [completeCount, setCompleteCount] = useState(0);

    const handleSelectionChange = useCallback((draftId: string, sel: CardSelections) => {
        const prev = cardSelectionsRef.current.get(draftId);
        cardSelectionsRef.current.set(draftId, sel);
        // Only update completeCount when isComplete flips
        if (!prev || prev.isComplete !== sel.isComplete) {
            setCompleteCount(() => {
                let count = 0;
                for (const s of cardSelectionsRef.current.values()) {
                    if (s.isComplete) {
                        count++;
                    }
                }
                return count;
            });
        }
    }, []);

    // ── Data loading ──

    const loadDrafts = useCallback(async () => {
        if (!visitId) {
            return;
        }
        try {
            const data = await fetchDrafts(visitId);
            setDrafts(data);
            if (initialCount === 0 && data.length > 0) {
                setInitialCount(data.length);
            }
        } catch (err) {
            console.error("Failed to load drafts:", err);
        } finally {
            setLoading(false);
        }
    }, [visitId]);

    useFocusEffect(
        useCallback(() => {
            loadDrafts();
        }, [loadDrafts])
    );

    // ── Auto-navigate when all drafts resolved ──

    const [allComplete, setAllComplete] = useState(false);
    useEffect(() => {
        if (!loading && drafts.length === 0) {
            setAllComplete(true);
            const timer = setTimeout(() => {
                if (!hasNavigatedRef.current) {
                    hasNavigatedRef.current = true;
                    if (router.canGoBack()) {
                        router.back();
                    } else {
                        router.replace(`/visit/${visitId}/medications`);
                    }
                }
            }, 2000);
            return () => clearTimeout(timer);
        }
        if (drafts.length > 0) {
            hasNavigatedRef.current = false;
        }
    }, [loading, drafts.length, router]);

    // ── Draft removal ──

    const removeDraft = useCallback((draftId: string) => {
        cardSelectionsRef.current.delete(draftId);
        setDrafts((prev) => prev.filter((d) => String(d.id) !== draftId));
        setReviewedCount((n) => n + 1);
    }, []);

    // ── Save (Decision 3: parent owns duplicate detection) ──

    const handleSave = useCallback(
        async (draftId: string): Promise<boolean> => {
            const sel = cardSelectionsRef.current.get(draftId);
            if (!sel) {
                return false;
            }

            setSavingDraftId(draftId);
            try {
                // Duplicate check
                const existingMeds = await fetchMedications(visitId!);
                const dup = checkDuplicate(existingMeds, sel.name, sel.isSupplement);
                if (dup) {
                    showDialog(
                        (sel.isSupplement ? "Supplement" : "Medication") + " Already on Profile",
                        `${sel.name} is already on the patient's profile. You cannot add it again.`,
                        [
                            {
                                text: "Discard Draft",
                                style: "destructive",
                                onPress: () => handleDiscard(draftId)
                            },
                            { text: "Cancel", style: "cancel" }
                        ]
                    );
                    return false;
                }

                // Decision 8: Use SELECTED values, not draft originals
                await promoteDraft(visitId!, draftId, {
                    medicationName: sel.name,
                    route: sel.route ?? undefined,
                    strength: sel.strength ?? undefined,
                    strengthNotApplicable: sel.strengthNotApplicable,
                    orderedDose: sel.dose ?? undefined,
                    frequency: sel.frequency ?? undefined,
                    administrationNotes: sel.adminNotes || undefined,
                    maxDailyDose: sel.maxDailyDose ?? undefined,
                    isSupplement: sel.isSupplement,
                    diagnosisCodes:
                        sel.icdCodes.length > 0
                            ? sel.icdCodes.map((c) => ({
                                  icdCode: c.code,
                                  description: c.description
                              }))
                            : undefined
                });
                // Remove AFTER successful API call — card animates out, then this removes from list
                setTimeout(() => {
                    removeDraft(draftId);
                    setSnackbar(`\u2713 ${sel.name} saved to profile`);
                }, 700); // wait for slide-out + collapse animations
                return true;
            } catch (err) {
                console.error("Failed to save draft:", err);
                setSnackbar("Failed to save. Please try again.");
                return false;
            } finally {
                setSavingDraftId(null);
            }
        },
        [visitId, removeDraft, showDialog]
    );

    // ── Discard ──

    const handleDiscard = useCallback(
        async (draftId: string) => {
            try {
                await deleteDraft(visitId!, draftId);
                removeDraft(draftId);
            } catch (err) {
                console.error("Failed to discard draft:", err);
            }
        },
        [visitId, removeDraft]
    );

    // ── Save All (Decision 3: sequential, with refreshed med list between each) ──

    const handleSaveAll = useCallback(async () => {
        const completeDraftIds: string[] = [];
        const incompleteDraftIds: string[] = [];

        for (const [draftId, sel] of cardSelectionsRef.current.entries()) {
            if (sel.isComplete) {
                completeDraftIds.push(draftId);
            } else {
                incompleteDraftIds.push(draftId);
            }
        }

        if (completeDraftIds.length === 0) {
            showDialog(
                "No Complete Items",
                "None of the items have all required fields filled yet."
            );
            return;
        }

        const msg =
            incompleteDraftIds.length > 0
                ? `${completeDraftIds.length} of ${drafts.length} medications are ready to save. ${incompleteDraftIds.length} item${incompleteDraftIds.length === 1 ? " is" : "s are"} missing required fields and will remain as drafts.`
                : `Save all ${completeDraftIds.length} medications to the patient's profile?`;

        showDialog("Save All Complete", msg, [
            { text: "Cancel", style: "cancel" },
            {
                text: `Save ${completeDraftIds.length}`,
                onPress: async () => {
                    let currentMeds: Medication[] = [];
                    try {
                        currentMeds = await fetchMedications(visitId!);
                    } catch {
                        // proceed without duplicate check
                    }

                    for (const draftId of completeDraftIds) {
                        const sel = cardSelectionsRef.current.get(draftId);
                        if (!sel) {
                            continue;
                        }

                        // Sequential duplicate check with updated med list
                        const dup = checkDuplicate(currentMeds, sel.name, sel.isSupplement);
                        if (dup) {
                            continue; // skip duplicates silently in bulk save
                        }

                        try {
                            await promoteDraft(visitId!, draftId, {
                                medicationName: sel.name,
                                route: sel.route ?? undefined,
                                strength: sel.strength ?? undefined,
                                strengthNotApplicable: sel.strengthNotApplicable,
                                orderedDose: sel.dose ?? undefined,
                                frequency: sel.frequency ?? undefined,
                                administrationNotes: sel.adminNotes || undefined,
                                maxDailyDose: sel.maxDailyDose ?? undefined,
                                isSupplement: sel.isSupplement,
                                diagnosisCodes:
                                    sel.icdCodes.length > 0
                                        ? sel.icdCodes.map((c) => ({
                                              icdCode: c.code,
                                              description: c.description
                                          }))
                                        : undefined
                            });
                            removeDraft(draftId);

                            // Refresh med list so next iteration sees the just-saved med
                            try {
                                currentMeds = await fetchMedications(visitId!);
                            } catch {
                                // continue with stale list
                            }
                        } catch (err) {
                            console.error(`Failed to save draft ${draftId}:`, err);
                        }
                    }
                    setSnackbar(
                        `\u2713 ${completeDraftIds.length} medication${completeDraftIds.length === 1 ? "" : "s"} saved`
                    );
                }
            }
        ]);
    }, [visitId, drafts.length, removeDraft, showDialog]);

    // ── Sort: incomplete meds first, complete second ──

    const sortedDrafts = [...drafts].sort((a, b) => {
        const aComplete =
            (a.isSupplement ?? false) || (a.orderedDose != null && a.frequency != null);
        const bComplete =
            (b.isSupplement ?? false) || (b.orderedDose != null && b.frequency != null);
        if (aComplete === bComplete) {
            return 0;
        }
        return aComplete ? 1 : -1;
    });

    const scrollRef = useRef<ScrollView>(null);

    // ── Loading state ──

    if (loading) {
        return (
            <SafeAreaView style={styles.safe}>
                <View style={styles.center}>
                    <ActivityIndicator size="large" />
                </View>
            </SafeAreaView>
        );
    }

    // ── All done state ──

    if (allComplete) {
        return (
            <SafeAreaView style={styles.safe}>
                <AllDraftsComplete />
            </SafeAreaView>
        );
    }

    // ── Main render ──

    return (
        <SafeAreaView style={styles.safe}>
            {/* Header */}
            <View style={styles.header}>
                <TouchableOpacity
                    style={styles.backRow}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 20 }}
                    activeOpacity={0.6}
                    onPress={() => {
                        if (router.canGoBack()) {
                            router.back();
                        } else {
                            router.replace(`/visit/${visitId}/medications`);
                        }
                    }}
                >
                    <MaterialCommunityIcons name="chevron-left" size={22} color={colors.teal500} />
                    <Text style={styles.backText}>Back</Text>
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Review Extracted Items</Text>
                <View style={styles.subHeaderRow}>
                    <Text style={styles.headerSubtitle}>
                        Found{" "}
                        <Text style={{ fontFamily: fonts.bold, color: colors.textPrimary }}>
                            {initialCount || drafts.length}
                        </Text>{" "}
                        items from your photos
                    </Text>
                    <Text style={styles.reviewedText}>
                        {reviewedCount} of {initialCount || drafts.length} reviewed
                    </Text>
                </View>
                <View style={styles.progressTrack}>
                    <View
                        style={[
                            styles.progressFill,
                            {
                                width:
                                    initialCount > 0
                                        ? `${(reviewedCount / initialCount) * 100}%`
                                        : "0%"
                            }
                        ]}
                    />
                </View>
            </View>

            {/* Cards */}
            <KeyboardAvoidingView
                style={{ flex: 1 }}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
            >
                <ScrollView
                    ref={scrollRef}
                    contentContainerStyle={styles.content}
                    keyboardShouldPersistTaps="handled"
                    automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
                >
                    {sortedDrafts.map((draft) => (
                        <EnrichedDraftCard
                            key={draft.id}
                            draft={draft}
                            visitId={visitId!}
                            enabled={activeEnrichments < MAX_CONCURRENT_LLM}
                            onLLMStart={onLLMStart}
                            onLLMEnd={onLLMEnd}
                            onSaved={handleSave}
                            onDiscard={() => handleDiscard(String(draft.id))}
                            onSelectionChange={(sel) =>
                                handleSelectionChange(String(draft.id), sel)
                            }
                            saving={savingDraftId === String(draft.id)}
                        />
                    ))}
                </ScrollView>
            </KeyboardAvoidingView>

            {/* Save All sticky button */}
            {completeCount > 0 && (
                <View style={styles.saveAllBar}>
                    <View style={styles.saveAllDivider} />
                    <TouchableOpacity style={styles.saveAllBtn} onPress={handleSaveAll}>
                        <Text style={styles.saveAllText}>Save All Complete ({completeCount})</Text>
                    </TouchableOpacity>
                </View>
            )}

            {/* Snackbar */}
            <Snackbar
                visible={snackbar !== null}
                onDismiss={() => setSnackbar(null)}
                duration={2500}
                style={styles.snackbar}
            >
                {snackbar ?? ""}
            </Snackbar>

            <ConfirmDialog {...dialogState} onDismiss={dismissDialog} />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: {
        flex: 1,
        backgroundColor: colors.gray50
    },
    center: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center"
    },
    header: {
        paddingHorizontal: 20,
        paddingTop: 8,
        paddingBottom: 16,
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    backRow: {
        flexDirection: "row",
        alignItems: "center",
        marginLeft: -spacing.xs,
        marginBottom: spacing.sm
    },
    backText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.teal500
    },
    headerTitle: {
        fontSize: 22,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    subHeaderRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8
    },
    headerSubtitle: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    reviewedText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.teal400
    },
    progressTrack: {
        height: 4,
        borderRadius: 9999,
        backgroundColor: "#EDEAF2",
        overflow: "hidden",
        marginTop: 8
    },
    progressFill: {
        height: "100%",
        borderRadius: 9999,
        backgroundColor: colors.teal400
    },
    content: {
        padding: 20,
        paddingBottom: 100
    },
    saveAllBar: {
        backgroundColor: colors.surface,
        paddingHorizontal: 20,
        paddingTop: 0,
        paddingBottom: 28
    },
    saveAllDivider: {
        height: 1,
        backgroundColor: colors.border,
        marginBottom: 12
    },
    saveAllBtn: {
        backgroundColor: colors.teal500,
        paddingVertical: 14,
        borderRadius: 20,
        alignItems: "center",
        ...shadows.brand
    },
    saveAllText: {
        color: "#FFFFFF",
        fontSize: 15,
        fontFamily: fonts.bold
    },
    snackbar: {
        backgroundColor: colors.textPrimary
    }
});
